import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  MessageSquare, 
  PieChart, 
  FileText,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Send,
  RefreshCw,
  Filter,
  Eye
} from "lucide-react";

export const AdminPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/admin/inquiries`, {
          params: { status: statusFilter },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ]);
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data.inquiries || []);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus, notes = "") => {
    try {
      await axios.put(`${API}/admin/inquiries/${inquiryId}`, {
        status: newStatus,
        admin_notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const sendResponse = async (inquiryId) => {
    if (!responseMessage.trim()) {
      toast.error("Please enter a response message");
      return;
    }
    setResponding(true);
    try {
      await axios.post(`${API}/admin/inquiries/${inquiryId}/respond`, {
        inquiry_id: inquiryId,
        response_message: responseMessage
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Response sent successfully!");
      setResponseMessage("");
      setSelectedInquiry(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to send response");
    } finally {
      setResponding(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-500/10 text-blue-500";
      case "in_progress": return "bg-yellow-500/10 text-yellow-500";
      case "resolved": return "bg-green-500/10 text-green-500";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new": return <AlertCircle className="w-4 h-4" />;
      case "in_progress": return <Clock className="w-4 h-4" />;
      case "resolved": return <CheckCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              Admin <span className="gold-text">Dashboard</span>
            </h1>
            <p className="text-muted-foreground">Manage inquiries and monitor platform activity</p>
          </div>

          <Button
            variant="outline"
            className="border-white/10"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Users</p>
              <p className="font-mono text-2xl font-bold" data-testid="total-users">
                {stats?.users?.total || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                {stats?.inquiries?.new > 0 && (
                  <Badge className="bg-red-500/20 text-red-400">
                    {stats.inquiries.new} new
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Inquiries</p>
              <p className="font-mono text-2xl font-bold" data-testid="total-inquiries">
                {stats?.inquiries?.total || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-green-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Portfolio Entries</p>
              <p className="font-mono text-2xl font-bold" data-testid="total-portfolios">
                {stats?.portfolios?.total || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Blog Posts</p>
              <p className="font-mono text-2xl font-bold" data-testid="total-posts">
                {stats?.blog_posts?.total || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Inquiries Management */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Manage Inquiries
              </CardTitle>

              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] bg-secondary/50 border-white/10" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : inquiries.length > 0 ? (
              <div className="space-y-4">
                {inquiries.map((inquiry, index) => (
                  <div
                    key={inquiry.inquiry_id || index}
                    className="p-4 bg-secondary/30 rounded-xl"
                    data-testid={`inquiry-${index}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold">{inquiry.name}</h3>
                          <Badge className={getStatusColor(inquiry.status)}>
                            {getStatusIcon(inquiry.status)}
                            <span className="ml-1 capitalize">{inquiry.status?.replace('_', ' ')}</span>
                          </Badge>
                          <Badge variant="outline" className="border-white/10">
                            {inquiry.inquiry_type}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {inquiry.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {inquiry.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(inquiry.created_at)}
                          </span>
                        </div>

                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {inquiry.message}
                        </p>

                        {inquiry.admin_notes && (
                          <p className="text-xs text-primary mt-2">
                            Notes: {inquiry.admin_notes}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Select
                          value={inquiry.status}
                          onValueChange={(v) => updateInquiryStatus(inquiry.inquiry_id, v)}
                        >
                          <SelectTrigger className="w-[140px] bg-secondary border-white/10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>

                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="border-white/10"
                              onClick={() => setSelectedInquiry(inquiry)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-card border-border max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="font-serif text-xl">
                                Inquiry from {inquiry.name}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Email:</span>
                                  <p>{inquiry.email}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Phone:</span>
                                  <p>{inquiry.phone}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Type:</span>
                                  <p className="capitalize">{inquiry.inquiry_type}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Status:</span>
                                  <Badge className={`${getStatusColor(inquiry.status)} ml-2`}>
                                    {inquiry.status?.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </div>

                              <div>
                                <span className="text-muted-foreground text-sm">Message:</span>
                                <p className="mt-1 p-3 bg-secondary/50 rounded-lg text-sm">
                                  {inquiry.message}
                                </p>
                              </div>

                              {inquiry.status !== "resolved" && (
                                <div className="space-y-3 pt-4 border-t border-border">
                                  <h4 className="font-semibold">Send Response</h4>
                                  <Textarea
                                    placeholder="Type your response here..."
                                    value={responseMessage}
                                    onChange={(e) => setResponseMessage(e.target.value)}
                                    className="bg-secondary/50 border-white/10 min-h-[120px]"
                                    data-testid="response-textarea"
                                  />
                                  <Button
                                    className="btn-primary w-full"
                                    onClick={() => sendResponse(inquiry.inquiry_id)}
                                    disabled={responding}
                                    data-testid="send-response-btn"
                                  >
                                    {responding ? (
                                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                      <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Send Response Email
                                  </Button>
                                </div>
                              )}

                              {inquiry.response_sent && (
                                <div className="p-3 bg-green-500/10 rounded-lg">
                                  <p className="text-green-500 text-sm flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" />
                                    Response sent on {formatDate(inquiry.responded_at)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No Inquiries Found</h3>
                <p className="text-muted-foreground text-sm">
                  {statusFilter ? "No inquiries match the selected filter" : "No inquiries received yet"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
